/**
 * Governance Proof Summary
 *
 * Demo Step 7: Final slide — "Every step was governed. Can your current
 * platform prove the same?"
 *
 * Aggregates all governance actions from the workflow execution into a
 * single compelling visualization that proves end-to-end governance.
 */

import React, { useState } from 'react';

export function GovernanceProofSummary({
  enforcementReport,     // From auto-insertion engine
  enforcementEvents,     // From WebSocket enforcement channel
  complianceEvents,      // From WebSocket compliance channel
  certificate,           // Generated compliance certificate
  workflowName,
  executionDurationMs,
}) {
  const [showDetail, setShowDetail] = useState(false);

  // Aggregate stats from all sources
  const stats = {
    enforcementNodesInserted: enforcementReport?.stats?.enforcementNodesAdded || 0,
    dpgScansPerformed: (enforcementEvents || []).filter(e => e.type === 'DPG_SCAN_RESULT').length,
    fieldsTokenized: (enforcementEvents || []).filter(e => e.type === 'TOKENIZATION_EVENT').length,
    policyDecisions: (enforcementEvents || []).filter(e => e.type === 'POLICY_GATE_DECISION').length,
    hashChainLength: certificate?.hashChain?.chainLength || 0,
    hashChainVerified: certificate?.hashChain?.integrityVerified || false,
    complianceRate: certificate?.enforcementSummary?.complianceRate || 'N/A',
    controlsPassed: certificate?.controlMapping?.filter(c => c.status === 'PASSED').length || 0,
    totalControls: certificate?.controlMapping?.length || 0,
  };

  const governanceLayers = [
    {
      layer: 1,
      name: 'Auto-Inserted Enforcement',
      description: `${stats.enforcementNodesInserted} protection nodes automatically woven into workflow`,
      icon: '🏗',
      color: '#0d9488',
      active: stats.enforcementNodesInserted > 0,
      detail: enforcementReport?.insertionReport?.map(r => r.reason).join(' • ') || '',
    },
    {
      layer: 2,
      name: 'Data Protection Guard',
      description: `${stats.dpgScansPerformed} scans performed, ${stats.fieldsTokenized} sensitive fields tokenized`,
      icon: '🛡',
      color: '#0a766b',
      active: stats.dpgScansPerformed > 0,
      detail: `PII detected and tokenized in real-time with AES-256-GCM envelope encryption`,
    },
    {
      layer: 3,
      name: 'Tokenized Data Vault',
      description: `${stats.fieldsTokenized} values encrypted with customer-managed KMS keys`,
      icon: '🔐',
      color: '#b0413a',
      active: stats.fieldsTokenized > 0,
      detail: 'HSM-backed envelope encryption ensures plaintext never leaves the process boundary',
    },
    {
      layer: 4,
      name: 'Policy Enforcement',
      description: `${stats.policyDecisions} policy decisions enforced at wire speed (<100ms)`,
      icon: '⚖',
      color: '#8c3a34',
      active: stats.policyDecisions > 0,
      detail: 'Every action node checked against policy rules, mandatory fields, and temporal windows',
    },
    {
      layer: 5,
      name: 'Tamper-Evident Audit Trail',
      description: `${stats.hashChainLength}-link SHA-256 hash chain — integrity ${stats.hashChainVerified ? 'VERIFIED' : 'UNVERIFIED'}`,
      icon: '📜',
      color: '#6b5c56',
      active: stats.hashChainLength > 0,
      detail: `Every enforcement event linked by SHA-256 hash chain from genesis to final hash`,
    },
    {
      layer: 6,
      name: 'Compliance Certificate',
      description: `${stats.controlsPassed}/${stats.totalControls} controls passed — ${stats.complianceRate} compliance rate`,
      icon: '✓',
      color: '#44403c',
      active: certificate != null,
      detail: certificate?.certificateHash ? `Certificate hash: ${certificate.certificateHash.substring(0, 24)}...` : '',
    },
  ];

  return (
    <div style={{
      background: 'linear-gradient(135deg, #0d9488 0%, #0a766b 50%, #44403c 100%)',
      borderRadius: 12,
      overflow: 'hidden',
      color: '#fff',
    }}>
      {/* Hero Header */}
      <div style={{
        padding: '24px 28px 20px',
        textAlign: 'center',
        borderBottom: '1px solid rgba(255,255,255,0.15)',
      }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>🏛</div>
        <h2 style={{
          fontSize: 22,
          fontWeight: 800,
          margin: '0 0 8px',
          letterSpacing: -0.5,
        }}>
          Every Step Was Protected
        </h2>
        <p style={{
          fontSize: 14,
          color: 'rgba(255,255,255,0.75)',
          margin: 0,
          maxWidth: 480,
          marginLeft: 'auto',
          marginRight: 'auto',
        }}>
          Complete protection proof for <strong style={{ color: '#ffffff' }}>{workflowName || 'Phishing Email Triage'}</strong>
          {executionDurationMs && (
            <span> — executed in {(executionDurationMs / 1000).toFixed(1)}s</span>
          )}
        </p>
      </div>

      {/* Governance Layer Stack */}
      <div style={{ padding: '16px 20px' }}>
        {governanceLayers.map((layer, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '10px 14px',
              marginBottom: 6,
              background: layer.active
                ? `rgba(${hexToRgb(layer.color)}, 0.15)`
                : 'rgba(255,255,255,0.03)',
              borderRadius: 8,
              border: `1px solid ${layer.active
                ? `rgba(${hexToRgb(layer.color)}, 0.3)`
                : 'rgba(255,255,255,0.06)'}`,
              cursor: layer.detail ? 'pointer' : 'default',
              transition: 'all 0.2s ease',
            }}
            onClick={() => layer.detail && setShowDetail(showDetail === i ? null : i)}
          >
            {/* Layer Number */}
            <div style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: layer.active ? layer.color : 'rgba(255,255,255,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 14,
              flexShrink: 0,
              transition: 'background 0.3s ease',
            }}>
              {layer.active ? layer.icon : layer.layer}
            </div>

            {/* Content */}
            <div style={{ flex: 1 }}>
              <div style={{
                fontWeight: 600,
                fontSize: 13,
                color: layer.active ? '#ffffff' : 'rgba(255,255,255,0.5)',
              }}>
                {layer.name}
              </div>
              <div style={{
                fontSize: 11,
                color: layer.active ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.35)',
                marginTop: 1,
              }}>
                {layer.description}
              </div>
              {showDetail === i && layer.detail && (
                <div style={{
                  fontSize: 10,
                  color: 'rgba(255,255,255,0.6)',
                  marginTop: 4,
                  fontFamily: 'monospace',
                  padding: '4px 8px',
                  background: 'rgba(0,0,0,0.15)',
                  borderRadius: 4,
                }}>
                  {layer.detail}
                </div>
              )}
            </div>

            {/* Status Badge */}
            <span style={{
              fontSize: 10,
              fontWeight: 600,
              padding: '2px 8px',
              borderRadius: 4,
              background: layer.active ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.06)',
              color: layer.active ? '#ffffff' : 'rgba(255,255,255,0.4)',
              flexShrink: 0,
            }}>
              {layer.active ? '✓ ACTIVE' : 'PENDING'}
            </span>
          </div>
        ))}
      </div>

      {/* Challenge Question */}
      <div style={{
        padding: '16px 28px 24px',
        textAlign: 'center',
        borderTop: '1px solid rgba(255,255,255,0.15)',
      }}>
        <div style={{
          fontSize: 16,
          fontWeight: 700,
          color: '#ffffff',
          fontStyle: 'italic',
        }}>
          "Can your current platform prove the same?"
        </div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 6 }}>
          Agentlets: Protected AI Agent Orchestration — trust is built in, not bolted on.
        </div>
      </div>
    </div>
  );
}

// Helper: Convert hex color to RGB for rgba()
function hexToRgb(hex) {
  const cleaned = hex.replace('#', '');
  const r = parseInt(cleaned.substring(0, 2), 16);
  const g = parseInt(cleaned.substring(2, 4), 16);
  const b = parseInt(cleaned.substring(4, 6), 16);
  return `${r},${g},${b}`;
}

export default GovernanceProofSummary;
